import { el, toast } from '../core/dom.js';

function safeRun(fn: () => void | Promise<void>): void {
  try {
    const ret = fn();
    if (ret && typeof (ret as Promise<unknown>).then === 'function') {
      (ret as Promise<unknown>).catch((err) => {
        console.error('Hero action failed:', err);
        toast('Something went wrong. Try again.');
      });
    }
  } catch (err) {
    console.error('Hero action threw:', err);
    toast('Something went wrong. Try again.');
  }
}

export type HeroState = 'normal' | 'urge_active' | 'session_active' | 'quota_low' | 'post_use';

export interface HeroAction {
  label: string;
  onClick: () => void | Promise<void>;
  variant?: 'primary' | 'default' | 'ghost' | 'urge';
}

export interface HeroActionCardOptions {
  state: HeroState;
  /** Short eyebrow above title. Plan name, habit label, status, etc. */
  eyebrow?: string | undefined;
  /** Hero title — the user's "next step" in plain language. */
  title: string;
  /** Optional supporting line. */
  subtitle?: string | undefined;
  /** Optional live value (e.g. timer). Caller updates DOM via the returned node. */
  liveValue?: string | undefined;
  /** Single primary action. */
  primary: HeroAction;
  /** Up to 3 secondary actions shown as a row of small buttons. */
  secondary?: HeroAction[];
}

export interface HeroActionCardHandle {
  root: HTMLElement;
  setLiveValue: (value: string) => void;
}

/**
 * One-CTA hero. The Now screen renders at most one of these at a time so the
 * primary action is unambiguous during cravings.
 */
export function createHeroActionCard(opts: HeroActionCardOptions): HeroActionCardHandle {
  const root = el('section', {
    class: `card hero-action hero-action--${opts.state}`,
    dataset: { heroState: opts.state },
  });
  if (opts.eyebrow) {
    root.appendChild(el('div', { class: 'eyebrow' }, opts.eyebrow));
  }
  root.appendChild(el('h1', { class: 'hero-action__title' }, opts.title));
  let liveNode: HTMLElement | null = null;
  if (opts.liveValue !== undefined) {
    liveNode = el('div', { class: 'hero-action__live' }, opts.liveValue);
    root.appendChild(liveNode);
  }
  if (opts.subtitle) {
    root.appendChild(el('p', { class: 'muted hero-action__subtitle' }, opts.subtitle));
  }
  const primaryClass = opts.primary.variant === 'urge'
    ? 'btn urge block hero-action__primary'
    : `btn ${opts.primary.variant ?? 'primary'} block hero-action__primary`;
  root.appendChild(el('button', {
    class: primaryClass,
    attrs: { type: 'button' },
    onclick: () => { safeRun(opts.primary.onClick); },
  }, opts.primary.label));

  if (opts.secondary && opts.secondary.length > 0) {
    const row = el('div', { class: 'hero-action__secondary' });
    for (const a of opts.secondary) {
      row.appendChild(el('button', {
        class: `btn ${a.variant ?? 'ghost'} small`,
        attrs: { type: 'button' },
        onclick: () => { safeRun(a.onClick); },
      }, a.label));
    }
    root.appendChild(row);
  }

  return {
    root,
    setLiveValue: (value: string) => {
      if (liveNode) liveNode.textContent = value;
    },
  };
}
